import {
  ConfirmDialog,
  deleteTrip,
  Forbidden,
  formatWhole,
  getPlan,
  NotFound,
  updateTrip,
  type Plan,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { day } from '../../lib/format';
import { TripDialog } from '../../components/trip/TripDialog';
import { useLive } from '../../app/live';
import { ArchivedBanner, useTripAccess } from '../../app/readOnly';
import { Areas } from './Areas';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/**
 * The trip's settings, away from the plan: its name, first day, budget and currency, and apart
 * from them, in a danger zone, deleting it.
 */
export function TripSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [editing, setEditing] = useState<'none' | 'trip' | 'delete'>('none');
  const [failed, setFailed] = useState(false);
  // A trip read only: its settings said, none changed; archived, taken out from here.
  const { locked, archived } = useTripAccess();

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        if (error instanceof Forbidden) void navigate(`/trips/${id}/view`, { replace: true });
        else setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, navigate, changed]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p role="alert">{t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}</p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan } = state;
  const close = () => {
    setEditing('none');
  };

  return (
    <div className="page settings-page">
      <h1>{t('settings.title')}</h1>
      {failed && (
        <p className="form-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}
      <section className="panel settings-card" aria-labelledby="settings-trip">
        <div className="settings-head">
          <h2 id="settings-trip">{t('settings.trip')}</h2>
          {!locked && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setEditing('trip');
              }}
            >
              {t('plan.edit')}
            </button>
          )}
        </div>
        <dl className="settings-facts">
          <div>
            <dt>{t('tripForm.name')}</dt>
            <dd>{plan.trip.name}</dd>
          </div>
          <div>
            <dt>{t('tripForm.purpose')}</dt>
            <dd>
              {plan.trip.purpose === null
                ? t('tripForm.purposeNone')
                : t(`purpose.${plan.trip.purpose}`)}
            </dd>
          </div>
          <div>
            <dt>{t('tripForm.start')}</dt>
            <dd>{day(plan.trip.start, lang)}</dd>
          </div>
          <div>
            <dt>{t('tripForm.budget')}</dt>
            <dd>{formatWhole(plan.trip.budget, lang)}</dd>
          </div>
          <div>
            <dt>{t('tripForm.currency')}</dt>
            <dd>{plan.trip.currency}</dd>
          </div>
        </dl>
      </section>

      <Areas plan={plan} editable={!locked && plan.role !== 'viewer'} />

      {/* Archived: put away read only, and taken out here as from the bar. */}
      <section className="panel settings-archive" aria-labelledby="settings-archive">
        <h2 id="settings-archive">{t('settings.archive')}</h2>
        <ArchivedBanner inline />
        {!archived && <p className="quiet">{t('settings.archiveHow')}</p>}
      </section>

      {!locked && (
        <section className="panel danger-zone" aria-labelledby="settings-danger">
          <h2 id="settings-danger">{t('settings.danger')}</h2>
          <div className="danger-row">
            <div>
              <p className="danger-what">{t('plan.delete')}</p>
              <p className="quiet">
                {plan.role === 'owner' ? t('plan.deleteBody') : t('settings.ownerOnly')}
              </p>
            </div>
            {plan.role === 'owner' && (
              <button
                type="button"
                className="button button-danger"
                onClick={() => {
                  setEditing('delete');
                }}
              >
                {t('plan.delete')}
              </button>
            )}
          </div>
        </section>
      )}

      {editing === 'trip' && (
        <TripDialog
          title={t('tripForm.editTitle')}
          submitLabel={t('tripForm.save')}
          initial={{
            name: plan.trip.name,
            start: plan.trip.start,
            budget: plan.trip.budget.minor,
            currency: plan.trip.currency,
            purpose: plan.trip.purpose,
          }}
          save={(input) => updateTrip(plan.trip.id, input)}
          onClose={close}
          onSaved={(next) => {
            setState({ kind: 'ready', plan: next });
            close();
          }}
        />
      )}
      <ConfirmDialog
        open={editing === 'delete'}
        title={t('plan.deleteQuestion', { name: plan.trip.name })}
        body={t('plan.deleteBody')}
        confirmLabel={t('plan.deleteConfirm')}
        onCancel={close}
        onConfirm={() => {
          deleteTrip(plan.trip.id).then(
            () => {
              void navigate('/');
            },
            () => {
              close();
              setFailed(true);
            },
          );
        }}
      />
    </div>
  );
}
